import {
    IonToast,
    IonBackButton,
    IonButton,
    IonContent,
    IonButtons,
    IonHeader,
    IonIcon,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonSegment,
    IonSegmentButton,
    IonSpinner,
    IonTitle,
    IonToolbar,
    RefresherEventDetail,
    useIonRouter
} from "@ionic/react";

import {
    checkmarkCircleOutline,
    americanFootballOutline,
    lockClosedOutline,
    peopleOutline,
    refreshOutline,
    trophyOutline
} from "ionicons/icons";
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import {
    useHistory,
    useLocation,
    useParams
} from "react-router-dom";

import Cookies from "js-cookie";

import "./FantasyTopPlayers.css";
import FantasyPlayerModal from "../components/FantasyPlayerModal";

import FantasyTradeOfferModal, {
    FantasyTradeCandidate
} from "../components/FantasyTradeOfferModal";

interface FantasyTopPlayer {
    id: string;
    name: string;
    positionId: string;
    position: string;
    teamId: string;
    teamName: string;
    seasonPoints: number;
    seasonGames: number;
    weekPoints: number;
    recentAverage: number;
    weekProjection: number;
    confidence: string;
    isAvailable: boolean;
    ownerTeamId: string;
    ownerName: string;
    isLocked: boolean;
}

interface FantasyRosterPlayer {
    id: string;
    name: string;
    positionId: string;
    position: string;
    teamId: string;
    teamName: string;
    slot: string;
    isLocked: boolean;
}

interface FantasyTopPlayersState {
    success: boolean;
    message?: string;

    league: {
        id: string;
        name: string;
    };

    week: {
        realRound: number;
        fantasyRound: number;
        status: string;
    };

    topPlayers: FantasyTopPlayer[];

    roster: {
        players: FantasyRosterPlayer[];
    };
}


const apiBaseUrl =
    "https://fantasy.ftblscore.com/create_league";

function getPositionLabel(
    player: Pick<
        FantasyTopPlayer,
        "position" | "positionId"
    >
): string {
    const position = String(player.position || "")
        .trim()
        .toUpperCase();

    if (position !== "") {
        return position;
    }

    const positions: Record<string, string> = {
        "1": "QB",
        "2": "RB",
        "3": "WR",
        "7": "K",
        "8": "DEF"
    };

    return positions[String(player.positionId || "")] || "—";
}

function formatPoints(value: number): string {
    return Number.isFinite(value)
        ? value.toFixed(1)
        : "0.0";
}

function getWeekStatusLabel(status?: string): string {
    const normalizedStatus = String(status || "")
        .trim()
        .toLowerCase();

    if (normalizedStatus === "live") {
        return "En vivo";
    }

    if (normalizedStatus === "scheduled") {
        return "Próximamente";
    }

    if (normalizedStatus === "final") {
        return "Finalizada";
    }

    return "Sin información";
}

const FantasyTopPlayers: React.FC = () => {
    const router = useIonRouter();
    const { id } = useParams<{ id: string }>();
    const history = useHistory();
    const location = useLocation();

    const [state, setState] =
        useState<FantasyTopPlayersState | null>(null);

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");
    const [positionFilter, setPositionFilter] =
        useState("all");
    const [
        rankingMode,
        setRankingMode
    ] = useState<"season" | "week">("season");

        const [
    selectedPlayerId,
    setSelectedPlayerId
] = useState<string | null>(null);

const [
    tradeRequestedPlayer,
    setTradeRequestedPlayer
] = useState<{
    id: string;
    name: string;
    position: string;
    realTeamName: string;
    ownerName: string;
} | null>(null);

const [
    isTradeOfferOpen,
    setIsTradeOfferOpen
] = useState(false);

const [
    isSubmittingTrade,
    setIsSubmittingTrade
] = useState(false);

const [message, setMessage] = useState("");

const pendingTradePlayerRef = useRef<{
    id: string;
    name: string;
    position: string;
    realTeamName: string;
    ownerName: string;
} | null>(null);

const pendingClaimPlayerIdRef = useRef("");

function goToLeagueHome(): void {
    if (!id) {
        return;
    }

    history.replace(`/fantasyLeague/${id}`);
}



    function goToFantasyTeam() {
        router.push(
            `/fantasyTeam/${id}`,
            "forward",
            "push"
        );
    }

    function goToFantasyGame() {
        router.push(
            `/fantasyGame/${id}`,
            "forward",
            "push"
        );
    }

    const loadTopPlayers = useCallback(
        async function (forceRefresh = false) {
            const username =
                Cookies.get("access_username") || "";

            const token =
                Cookies.get("access_token") || "";

            if (!username || !token) {
                router.push(
                    "/home",
                    "root",
                    "replace"
                );

                return;
            }

            try {
                setError("");

                if (forceRefresh) {
                    setRefreshing(true);
                } else {
                    setLoading(true);
                }

                const response = await fetch(
                    `${apiBaseUrl}/control_FantasyPlayersState.php` +
                        `?id=${encodeURIComponent(id)}` +
                        `&t=${Date.now()}`,
                    {
                        method: "GET",
                        credentials: "include",
                        cache: "no-store",
                        headers: {
                            Accept: "application/json",
                            "X-Access-Username": username,
                            "X-Access-Token": token
                        }
                    }
                );

                const rawResponse =
                    await response.text();

                let result: FantasyTopPlayersState;

                try {
                    result = JSON.parse(rawResponse);
                } catch {
                    throw new Error(
                        rawResponse.trim()
                            ? `El servidor devolvió una respuesta no válida. HTTP ${response.status}.`
                            : `El servidor devolvió una respuesta vacía. HTTP ${response.status}.`
                    );
                }

                if (!response.ok || !result.success) {
                    throw new Error(
                        result.message ||
                            "No fue posible cargar los líderes Fantasy."
                    );
                }

                setState(result);
            } catch (requestError) {
                console.error(
                    "No fue posible cargar líderes Fantasy:",
                    requestError
                );

                setError(
                    requestError instanceof Error
                        ? requestError.message
                        : "No fue posible cargar los líderes Fantasy."
                );
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [id, router]
    );

    useEffect(
        function () {
            loadTopPlayers();
        },
        [loadTopPlayers]
    );

    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadTopPlayers(true);
        event.detail.complete();
    }

function handleRequestTrade(
    player: {
        id: string;
        name: string;
        position: string;
        realTeamName: string;
        ownerName: string;
    }
): void {
    if (!player.id) {
        setMessage(
            "No fue posible identificar al jugador para el intercambio."
        );

        return;
    }

    /*
        Guarda el jugador temporalmente. El modal de trade se abrirá
        hasta que Ionic confirme que la ficha del jugador ya cerró.
    */
    pendingTradePlayerRef.current = {
        id: player.id,
        name: player.name || "Jugador",
        position: player.position || "—",
        realTeamName:
            player.realTeamName || "Sin equipo",
        ownerName: player.ownerName || ""
    };

    setSelectedPlayerId(null);
}

function handlePlayerModalDismiss(): void {
    setSelectedPlayerId(null);

    const claimPlayerId =
        pendingClaimPlayerIdRef.current;

    /*
        Claim pendiente: la navegación ocurre sólo después de que
        FantasyPlayerModal terminó de cerrarse.
    */
    if (claimPlayerId) {
        pendingClaimPlayerIdRef.current = "";

        window.requestAnimationFrame(function () {
            router.push(
                `/fantasyPlayers/${id}?playerId=${encodeURIComponent(
                    claimPlayerId
                )}`,
                "forward",
                "push"
            );
        });

        return;
    }

    const pendingTradePlayer =
        pendingTradePlayerRef.current;

    if (!pendingTradePlayer) {
        return;
    }

    pendingTradePlayerRef.current = null;

    /*
        Trade pendiente: abre el segundo modal después de retirar el
        overlay de la ficha del jugador.
    */
    window.requestAnimationFrame(function () {
        setTradeRequestedPlayer(pendingTradePlayer);
        setIsTradeOfferOpen(true);
    });
}

async function createTrade(
    offeredPlayerId: string
) {
    if (!tradeRequestedPlayer || isSubmittingTrade) {
        return;
    }

    try {
        setIsSubmittingTrade(true);

        const response = await fetch(
            `${apiBaseUrl}/control_CreateFantasyTrade.php`,
            {
                method: "POST",
                credentials: "include",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    "X-Access-Username":
                        Cookies.get(
                            "access_username"
                        ) || "",
                    "X-Access-Token":
                        Cookies.get(
                            "access_token"
                        ) || ""
                },
                body: JSON.stringify({
                    leagueId: id,
                    offeredPlayerId,
                    requestedPlayerId:
                        tradeRequestedPlayer.id
                })
            }
        );

        const rawResponse = await response.text();

        let result: {
            success?: boolean;
            message?: string;
        };

        try {
            result = JSON.parse(rawResponse);
        } catch {
            throw new Error(
                rawResponse.trim()
                    ? `El servidor devolvió una respuesta no válida. HTTP ${response.status}.`
                    : `El servidor devolvió una respuesta vacía. HTTP ${response.status}.`
            );
        }

        if (!response.ok || !result.success) {
            throw new Error(
                result.message ||
                    "No fue posible enviar la propuesta."
            );
        }

        setIsTradeOfferOpen(false);
        setTradeRequestedPlayer(null);
        setSelectedPlayerId(null);

        setMessage(
            result.message ||
                "Solicitud de intercambio enviada."
        );

        await loadTopPlayers(true);
    } catch (requestError) {
        console.error(
            "No fue posible crear intercambio:",
            requestError
        );

        setMessage(
            requestError instanceof Error
                ? requestError.message
                : "No fue posible enviar la propuesta."
        );
    } finally {
        setIsSubmittingTrade(false);
    }
}

const filteredPlayers = useMemo(
    function (): FantasyTopPlayer[] {
        const players = state?.topPlayers || [];

        const filtered = positionFilter === "all"
            ? players
            : players.filter(function (player) {
                return (
                    getPositionLabel(player) ===
                    positionFilter
                );
            });

        return [...filtered].sort(function (
            left,
            right
        ): number {
            const primaryComparison =
                rankingMode === "season"
                    ? right.seasonPoints - left.seasonPoints
                    : right.weekPoints - left.weekPoints;

            if (primaryComparison !== 0) {
                return primaryComparison;
            }

            /*
                Desempate 1:
                mayor proyección de la jornada.
            */
            const projectionComparison =
                right.weekProjection - left.weekProjection;

            if (projectionComparison !== 0) {
                return projectionComparison;
            }

            /*
                Desempate 2:
                mayor promedio reciente.
            */
            const recentAverageComparison =
                right.recentAverage - left.recentAverage;

            if (recentAverageComparison !== 0) {
                return recentAverageComparison;
            }

            /*
                Desempate final:
                nombre en orden alfabético.
            */
            return left.name.localeCompare(
                right.name,
                "es",
                {
                    sensitivity: "base"
                }
            );
        });
    },
    [
        positionFilter,
        rankingMode,
        state?.topPlayers
    ]
);

const tradeCandidates = useMemo(
    function (): FantasyTradeCandidate[] {
        return (
            state?.roster.players || []
        ).map(function (player) {
            return {
                id: player.id,

                name: player.name,

                position:
                    player.position ||
                    getPositionLabel(player),

                realTeamName:
                    player.teamName ||
                    "Sin equipo",

                isLocked: Boolean(player.isLocked)
            };
        });
    },
    [state?.roster.players]
);
const myRosterPlayerIds = useMemo(
    function (): Set<string> {
        return new Set(
            (state?.roster.players || []).map(
                function (player) {
                    return player.id;
                }
            )
        );
    },
    [state?.roster.players]
);
const selectedTopPlayer = useMemo(
    function (): FantasyTopPlayer | null {
        if (!selectedPlayerId) {
            return null;
        }

        return (
            state?.topPlayers.find(
                function (player) {
                    return player.id === selectedPlayerId;
                }
            ) || null
        );
    },
    [selectedPlayerId, state?.topPlayers]
);
const isMyPlayer = Boolean(
    selectedTopPlayer &&
    myRosterPlayerIds.has(selectedTopPlayer.id)
);

const isFreeAgent = Boolean(
    selectedTopPlayer?.isAvailable
);

const belongsToAnotherTeam = Boolean(
    selectedTopPlayer &&
    !selectedTopPlayer.isAvailable &&
    !isMyPlayer
);

function handleAddFromTopPlayers(
    playerId: string
): void {
    if (!playerId) {
        setMessage(
            "No fue posible identificar al jugador para reclamar."
        );

        return;
    }

    /*
        No navegamos todavía. Primero cerramos FantasyPlayerModal
        para que Ionic retire correctamente su overlay y backdrop.
    */
    pendingClaimPlayerIdRef.current = playerId;

    setSelectedPlayerId(null);
}

function handleDropFromTopPlayers(
    playerId: string
): void {
    router.push(
        `/fantasyDropPlayers/${id}?playerId=${encodeURIComponent(
            playerId
        )}`,
        "forward",
        "push"
    );
}

const activePlayersSubmenu = useMemo(
    function (): string {
        if (
            location.pathname.includes(
                "/fantasyDropPlayers/"
            )
        ) {
            return "drop";
        }

        if (
            location.pathname.includes(
                "/fantasyTopPlayers/"
            )
        ) {
            return "leaders";
        }

        return "add";
    },
    [location.pathname]
);

    return (
        <IonPage>
            <IonHeader translucent>
<IonToolbar className="fantasy-top-toolbar">
    <IonButtons slot="start">
        <IonBackButton
            defaultHref={`/fantasyPlayers/${id}`}
            text=""
        />
    </IonButtons>

    <IonTitle>Líderes</IonTitle>
</IonToolbar>


                <IonToolbar className="fantasy-top-navigation">
                    <IonSegment
                        value="players"
                        onIonChange={function (event) {
                            const selectedTab =
                                event.detail.value;

                            if (selectedTab === "home") {
                                goToLeagueHome();
                                return;
                            }

                            if (selectedTab === "team") {
                                goToFantasyTeam();
                                return;
                            }

                            if (selectedTab === "game") {
                                goToFantasyGame();
                            }
                        }}
                    >
                        <IonSegmentButton value="home">
                            Inicio
                        </IonSegmentButton>

                        <IonSegmentButton value="players">
                            Jugadores
                        </IonSegmentButton>

                        <IonSegmentButton value="team">
                            Mi equipo
                        </IonSegmentButton>

                        <IonSegmentButton value="game">
                            Juego
                        </IonSegmentButton>
                    </IonSegment>
                </IonToolbar>
<IonToolbar className="fantasy-players-subnavigation">
    <IonSegment
        value={activePlayersSubmenu}
        onIonChange={function (event) {
            const selectedTab = String(
                event.detail.value || "add"
            );

            if (selectedTab === activePlayersSubmenu) {
                return;
            }

            if (selectedTab === "add") {
                history.replace(
                    `/fantasyPlayers/${id}`
                );

                return;
            }

            if (selectedTab === "drop") {
                history.replace(
                    `/fantasyDropPlayers/${id}`
                );

                return;
            }

            if (selectedTab === "leaders") {
                history.replace(
                    `/fantasyTopPlayers/${id}`
                );
            }
        }}
    >
        <IonSegmentButton value="add">
            Agregar
        </IonSegmentButton>

        <IonSegmentButton value="drop">
            Soltar
        </IonSegmentButton>

        <IonSegmentButton value="leaders">
            Líderes
        </IonSegmentButton>
    </IonSegment>
</IonToolbar>
            </IonHeader>

            <IonContent
                fullscreen
                className="fantasy-top-content"
            >
                <IonRefresher
                    slot="fixed"
                    onIonRefresh={handleRefresh}
                >
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Actualizando líderes..."
                    />
                </IonRefresher>

                {loading && (
                    <IonProgressBar
                        type="indeterminate"
                        className="fantasy-top-progress"
                    />
                )}

                {!loading && error && (
                    <main className="fantasy-top-main">
                        <div className="fantasy-top-empty">
                            <IonIcon icon={refreshOutline} />

                            <strong>
                                No pudimos cargar los líderes
                            </strong>

                            <span>{error}</span>

                            <IonButton
                                size="small"
                                fill="outline"
                                onClick={function () {
                                    loadTopPlayers(true);
                                }}
                            >
                                Reintentar
                            </IonButton>
                        </div>
                    </main>
                )}

                {!loading && !error && state && (
                    <><section className="fantasy-top-hero">
                        <div className="fantasy-top-hero__icon">
                            <IonIcon icon={trophyOutline} />
                        </div>

                        <div>
                            <span>{state.league.name}</span>
                            <h1>Líderes de la liga</h1>
                            <p>
                                Consulta a los jugadores con mejor rendimiento.
                            </p>
                        </div>
                    </section>
                    <main className="fantasy-top-main">

<section className="fantasy-top-ranking-mode">
    <div className="fantasy-top-ranking-mode__heading">
        <span>ORDENAR POR</span>
    </div>

    <IonSegment
        value={rankingMode}
        onIonChange={function (event) {
            const value = String(
                event.detail.value || "season"
            );

            if (
                value === "season" ||
                value === "week"
            ) {
                setRankingMode(value);
            }
        }}
    >
        <IonSegmentButton value="season">
            Temporada
        </IonSegmentButton>

        <IonSegmentButton value="week">
            Jornada
        </IonSegmentButton>
    </IonSegment>
</section>

                            <section className="fantasy-top-filter">
                                <IonSegment
                                    value={positionFilter}
                                    scrollable
                                    onIonChange={function (event) {
                                        setPositionFilter(
                                            String(
                                                event.detail.value ||
                                                "all"
                                            )
                                        );
                                    } }
                                >
                                    <IonSegmentButton value="all">
                                        Todos
                                    </IonSegmentButton>

                                    <IonSegmentButton value="QB">
                                        QB
                                    </IonSegmentButton>

                                    <IonSegmentButton value="RB">
                                        RB
                                    </IonSegmentButton>

                                    <IonSegmentButton value="WR">
                                        WR
                                    </IonSegmentButton>

                                    <IonSegmentButton value="K">
                                        K
                                    </IonSegmentButton>

                                    <IonSegmentButton value="DEF">
                                        DEF
                                    </IonSegmentButton>
                                </IonSegment>
                            </section>

                            <section className="fantasy-top-section">
                                <div className="fantasy-top-section__heading">
                                    <div>
                                        <span>CLASIFICACIÓN</span>

                                        <h2>
                                            {rankingMode === "season"
                                                ? positionFilter === "all"
                                                    ? "Líderes de la temporada"
                                                    : `Líderes de temporada · ${positionFilter}`
                                                : positionFilter === "all"
                                                    ? `Líderes de la jornada ${state.week.realRound}`
                                                    : `Jornada ${state.week.realRound} · ${positionFilter}`}
                                        </h2>

                                    </div>

                                    {refreshing && (
                                        <IonSpinner name="crescent" />
                                    )}
                                </div>

                                {filteredPlayers.length === 0 ? (
                                    <div className="fantasy-top-empty">
                                        <IonIcon
                                            icon={americanFootballOutline} />

                                        <strong>
                                            No hay jugadores para mostrar
                                        </strong>

                                        <span>
                                            No existen registros para
                                            este filtro.
                                        </span>
                                    </div>
                                ) : (
                                    <div className="fantasy-top-list">
                                        {filteredPlayers.map(
                                            function (
                                                player,
                                                index
                                            ) {
                                                const position = getPositionLabel(
                                                    player
                                                );

                                                return (
                                                    <article
    key={player.id}
    role="button"
    tabIndex={0}
    className="fantasy-top-card fantasy-top-card--selectable"
    onClick={function () {
        setSelectedPlayerId(player.id);
    }}
    onKeyDown={function (event) {
        if (
            event.key === "Enter" ||
            event.key === " "
        ) {
            event.preventDefault();

            setSelectedPlayerId(player.id);
        }
    }}
>

                                                        <div className="fantasy-top-card__rank">
                                                            #{index + 1}
                                                        </div>

                                                        <span
                                                            className={`fantasy-top-card__position fantasy-top-card__position--${position.toLowerCase()}`}
                                                        >
                                                            {position}
                                                        </span>

                                                        <div className="fantasy-top-card__details">
                                                            <strong>
                                                                {player.name}
                                                            </strong>

                                                            <small>
                                                                {player.teamName ||
                                                                    "Sin equipo"}
                                                            </small>

                                                            <div className="fantasy-top-card__status">
                                                                {player.isAvailable ? (
                                                                    <>
                                                                        <IonIcon
                                                                            icon={checkmarkCircleOutline} />

                                                                        Disponible
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <IonIcon
                                                                            icon={peopleOutline} />
{player.ownerName || "En roster"}
                                                                        
                                                                    </>
                                                                )}

                                                                {player.isLocked && (
                                                                    <>
                                                                        <span>
                                                                            ·
                                                                        </span>

                                                                        <IonIcon
                                                                            icon={lockClosedOutline} />

                                                                        Bloqueado
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="fantasy-top-card__stats">
                                                            <div>
                                                                <span>
                                                                    {rankingMode === "season"
                                                                        ? "PTS"
                                                                        : "PTS JOR."}
                                                                </span>

                                                                <strong>
                                                                    {formatPoints(
                                                                        rankingMode === "season"
                                                                            ? player.seasonPoints
                                                                            : player.weekPoints
                                                                    )}
                                                                </strong>
                                                            </div>


                                                            <div>
                                                                <span>
                                                                    PROM.
                                                                </span>

                                                                <strong>
                                                                    {formatPoints(
                                                                        player.recentAverage
                                                                    )}
                                                                </strong>
                                                            </div>

                                                            <div>
                                                                <span>
                                                                    PROY.
                                                                </span>

                                                                <strong>
                                                                    {formatPoints(
                                                                        player.weekProjection
                                                                    )}
                                                                </strong>
                                                            </div>
                                                        </div>
                                                    </article>
                                                );
                                            }
                                        )}
                                    </div>
                                )}
                            </section>
                        </main></>
                )}
            </IonContent>
<FantasyPlayerModal
    leagueId={id || ""}
    playerId={selectedPlayerId || ""}
    isOpen={selectedPlayerId !== null}
    onDismiss={handlePlayerModalDismiss}
    onRequestTrade={handleRequestTrade}
    onClaimPlayer={handleAddFromTopPlayers}
    onDropPlayer={handleDropFromTopPlayers}
/>

<FantasyTradeOfferModal
    isOpen={isTradeOfferOpen}
    requestedPlayer={tradeRequestedPlayer}
    candidates={tradeCandidates}
    isSubmitting={isSubmittingTrade}
    onDismiss={function () {
        if (isSubmittingTrade) {
            return;
        }

        setIsTradeOfferOpen(false);
        setTradeRequestedPlayer(null);
    }}
    onSubmit={createTrade}
/>

<IonToast
    isOpen={Boolean(message)}
    message={message}
    duration={3400}
    position="bottom"
    onDidDismiss={function () {
        setMessage("");
    }}
/>


        </IonPage>
    );
};

export default FantasyTopPlayers;
